import { createSignal, type Accessor } from 'solid-js';
import type { QueryClient } from '@tanstack/solid-query';

import { fxWorkspaceReferencesQueryKey } from './fx-workspace-query';
import { createWorkspaceLoadTokens } from './fx-workspace-scope';

interface PendingReferenceDiff {
  readonly added: Set<string>;
  readonly removed: Set<string>;
}

function mergeServerReferencesWithPending(
  serverRefs: readonly string[],
  pending: PendingReferenceDiff | undefined
): Set<string> {
  const merged = new Set(serverRefs);
  if (pending === undefined) {
    return merged;
  }

  for (const refTrn of pending.removed) {
    merged.delete(refTrn);
  }
  for (const refTrn of pending.added) {
    merged.add(refTrn);
  }
  return merged;
}

export interface FxWorkspaceReferenceStore {
  readonly loadedReferenceWorkspaceIds: Accessor<ReadonlySet<string>>;
  bumpReferenceToggleToken(tokenKey: string): number;
  isLatestReferenceToggleToken(tokenKey: string, token: number): boolean;
  isReferenceVisible(workspaceId: string, refTrn: string): boolean;
  workspaceReferenceCount(workspaceId: string): number;
  getWorkspaceReferences(workspaceId: string): ReadonlySet<string>;
  areWorkspaceReferencesLoaded(workspaceId: string): boolean;
  setWorkspaceReferenceSet(
    workspaceId: string,
    refTrns: ReadonlySet<string>
  ): void;
  cacheWorkspaceReferences(
    workspaceId: string,
    refTrns: ReadonlySet<string>
  ): void;
  applyWorkspaceReferences(
    workspaceId: string,
    refTrns: readonly string[]
  ): void;
  recordPendingToggle(
    workspaceId: string,
    refTrn: string,
    wasVisible: boolean
  ): void;
  revertPendingToggle(
    workspaceId: string,
    refTrn: string,
    wasVisible: boolean
  ): void;
  referenceToggleTokenKey(workspaceId: string, refTrn: string): string;
  clearWorkspace(workspaceId: string): void;
  resetRepositoryScope(): void;
}

export function createFxWorkspaceReferenceStore(options: {
  queryClient: QueryClient;
  fetchKey: Accessor<string | undefined>;
}): FxWorkspaceReferenceStore {
  const [workspaceReferences, setWorkspaceReferences] = createSignal<
    Record<string, ReadonlySet<string>>
  >({});
  const [loadedReferenceWorkspaceIds, setLoadedReferenceWorkspaceIds] =
    createSignal<ReadonlySet<string>>(new Set());
  const referenceToggleTokens = createWorkspaceLoadTokens();
  const pendingReferenceDiffs = new Map<string, PendingReferenceDiff>();

  const getPendingReferenceDiff = (
    workspaceId: string
  ): PendingReferenceDiff => {
    const existing = pendingReferenceDiffs.get(workspaceId);
    if (existing !== undefined) {
      return existing;
    }

    const created: PendingReferenceDiff = {
      added: new Set<string>(),
      removed: new Set<string>(),
    };
    pendingReferenceDiffs.set(workspaceId, created);
    return created;
  };

  const setWorkspaceReferenceSet = (
    workspaceId: string,
    refTrns: ReadonlySet<string>
  ): void => {
    setWorkspaceReferences((previous) => ({
      ...previous,
      [workspaceId]: refTrns,
    }));
  };

  const cacheWorkspaceReferences = (
    workspaceId: string,
    refTrns: ReadonlySet<string>
  ): void => {
    const key = options.fetchKey();
    if (key === undefined) {
      return;
    }

    options.queryClient.setQueryData(
      fxWorkspaceReferencesQueryKey(key, workspaceId),
      [...refTrns]
    );
  };

  const recordPendingToggle = (
    workspaceId: string,
    refTrn: string,
    wasVisible: boolean
  ): void => {
    const diff = getPendingReferenceDiff(workspaceId);
    if (wasVisible) {
      diff.added.delete(refTrn);
      diff.removed.add(refTrn);
      return;
    }

    diff.removed.delete(refTrn);
    diff.added.add(refTrn);
  };

  const revertPendingToggle = (
    workspaceId: string,
    refTrn: string,
    wasVisible: boolean
  ): void => {
    const diff = pendingReferenceDiffs.get(workspaceId);
    if (diff === undefined) {
      return;
    }

    if (wasVisible) {
      diff.removed.delete(refTrn);
      diff.added.add(refTrn);
      return;
    }

    diff.added.delete(refTrn);
  };

  const applyWorkspaceReferences = (
    workspaceId: string,
    refTrns: readonly string[]
  ): void => {
    if (loadedReferenceWorkspaceIds().has(workspaceId)) {
      return;
    }

    const merged = mergeServerReferencesWithPending(
      refTrns,
      pendingReferenceDiffs.get(workspaceId)
    );
    pendingReferenceDiffs.delete(workspaceId);
    setWorkspaceReferenceSet(workspaceId, merged);
    setLoadedReferenceWorkspaceIds(
      (previous) => new Set<string>([...previous, workspaceId])
    );
    cacheWorkspaceReferences(workspaceId, merged);
  };

  const referenceToggleTokenKey = (
    workspaceId: string,
    refTrn: string
  ): string => `${workspaceId}\0${refTrn}`;

  const clearWorkspace = (workspaceId: string): void => {
    setWorkspaceReferences((previous) => {
      const next = { ...previous };
      delete next[workspaceId];
      return next;
    });
    setLoadedReferenceWorkspaceIds((previous) => {
      const next = new Set(previous);
      next.delete(workspaceId);
      return next;
    });
    pendingReferenceDiffs.delete(workspaceId);
    referenceToggleTokens.delete(workspaceId);
    const repositoryKey = options.fetchKey();
    if (repositoryKey !== undefined) {
      options.queryClient.removeQueries({
        queryKey: fxWorkspaceReferencesQueryKey(repositoryKey, workspaceId),
      });
    }
  };

  const resetRepositoryScope = (): void => {
    referenceToggleTokens.clear();
    pendingReferenceDiffs.clear();
    setWorkspaceReferences({});
    setLoadedReferenceWorkspaceIds(new Set<string>());
  };

  return {
    loadedReferenceWorkspaceIds,
    bumpReferenceToggleToken: (tokenKey: string): number =>
      referenceToggleTokens.bump(tokenKey),
    isLatestReferenceToggleToken: (tokenKey: string, token: number): boolean =>
      referenceToggleTokens.isLatest(tokenKey, token),
    isReferenceVisible: (workspaceId: string, refTrn: string): boolean =>
      workspaceReferences()[workspaceId]?.has(refTrn) ?? false,
    workspaceReferenceCount: (workspaceId: string): number =>
      workspaceReferences()[workspaceId]?.size ?? 0,
    getWorkspaceReferences: (workspaceId: string): ReadonlySet<string> =>
      workspaceReferences()[workspaceId] ?? new Set<string>(),
    areWorkspaceReferencesLoaded: (workspaceId: string): boolean =>
      loadedReferenceWorkspaceIds().has(workspaceId),
    setWorkspaceReferenceSet,
    cacheWorkspaceReferences,
    applyWorkspaceReferences,
    recordPendingToggle,
    revertPendingToggle,
    referenceToggleTokenKey,
    clearWorkspace,
    resetRepositoryScope,
  };
}
